import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import useInvitations from '../../hooks/useInvitations';
import { useToast } from '../../components/ui/ToastManager';

const InvitationManager = ({ salonId }) => {
  const { selectedSalonId, currentUser } = useUser();
  const effectiveSalonId = salonId || selectedSalonId;
  const { showToast } = useToast();
  
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Usar o hook de convites
  const {
    invitations,
    loading,
    error: invitationsError,
    sendInvite,
    cancelInviteById,
    resendInviteById,
    checkPermission
  } = useInvitations(effectiveSalonId);

  const [isAdmin, setIsAdmin] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    role: 'professional',
    permissions: [
      'manage_appointments',
      'view_analytics'
    ],
    professionalData: {
      specialty: '',
      bio: ''
    }
  });
  
  // Verificar permissões do usuário e mostrar mensagem de erro se o hook também encontrou problemas
  useEffect(() => {
    const verifyPermission = async () => {
      try {
        const hasPermission = await checkPermission();
        setIsAdmin(hasPermission);
        
        if (!hasPermission) {
          console.warn('Usuário não tem permissão para gerenciar convites');
        }
        
        if (invitationsError) {
          showToast(invitationsError, 'error');
        }
      } catch (error) {
        console.error('Erro ao verificar permissões:', error);
        setIsAdmin(false);
      }
    };
    
    if (currentUser) {
      verifyPermission();
    } else {
      setIsAdmin(false);
    }
  }, [checkPermission, invitationsError, showToast, currentUser]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('professionalData.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        professionalData: {
          ...prev.professionalData,
          [field]: value
        }
      }));
    } else if (name === 'permissions') {
      // Checkbox para permissões (multiple)
      const permissionsArray = [...formData.permissions];
      if (e.target.checked) {
        permissionsArray.push(e.target.value);
      } else {
        const index = permissionsArray.indexOf(e.target.value);
        if (index !== -1) {
          permissionsArray.splice(index, 1);
        }
      }
      setFormData(prev => ({
        ...prev,
        permissions: permissionsArray
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      showToast('Por favor, informe um email', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Enviar convite usando o hook
      await sendInvite(
        formData.email,
        formData.role,
        formData.permissions,
        formData.role === 'professional' ? formData.professionalData : null
      );
      
      // Limpar formulário
      setFormData({
        email: '',
        role: 'professional',
        permissions: [
          'manage_appointments',
          'view_analytics'
        ],
        professionalData: {
          specialty: '',
          bio: ''
        }
      });
      
      setShowForm(false);
      showToast(`Convite enviado para ${formData.email}`, 'success');
    } catch (error) {
      console.error('Erro ao enviar convite:', error);
      showToast(error.message || 'Erro ao enviar convite', 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancel = async (invitationId) => {
    try {
      await cancelInviteById(invitationId);
      showToast('Convite cancelado com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao cancelar convite:', error);
      showToast(error.message || 'Erro ao cancelar convite', 'error');
    }
  };
  
  const handleResend = async (invitationId) => {
    try {
      await resendInviteById(invitationId);
      showToast('Convite reenviado com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao reenviar convite:', error);
      showToast(error.message || 'Erro ao reenviar convite', 'error');
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="text-center py-6">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Restrito</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para gerenciar convites de usuários.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Gerenciar Convites</h3>
          <p className="mt-1 text-sm text-gray-500">
            Envie convites para novos profissionais e gestores
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Novo Convite
          </button>
        )}
      </div>
      
      {invitationsError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {invitationsError}
        </div>
      )}
      
      {/* Formulário de envio de convites */}
      {showForm && (
        <div className="mb-8 bg-gray-50 p-4 rounded-md">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="email@exemplo.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Tipo de Usuário
                </label>
                <div className="mt-1 flex items-center space-x-4">
                  <div className="flex items-center">
                    <input
                      id="role-professional"
                      name="role"
                      type="radio"
                      value="professional"
                      checked={formData.role === 'professional'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="role-professional" className="ml-2 block text-sm text-gray-700">
                      Profissional
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="role-manager"
                      name="role"
                      type="radio"
                      value="manager"
                      checked={formData.role === 'manager'}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <label htmlFor="role-manager" className="ml-2 block text-sm text-gray-700">
                      Gestor
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Campos específicos para profissional */}
              {formData.role === 'professional' && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="professionalData.specialty" className="block text-sm font-medium text-gray-700">
                      Especialidade
                    </label>
                    <input
                      type="text"
                      id="professionalData.specialty"
                      name="professionalData.specialty"
                      value={formData.professionalData.specialty}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Ex: Cabeleireiro"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="professionalData.bio" className="block text-sm font-medium text-gray-700">
                      Biografia
                    </label>
                    <textarea
                      id="professionalData.bio"
                      name="professionalData.bio"
                      value={formData.professionalData.bio}
                      onChange={handleChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Breve descrição do profissional"
                    />
                  </div>
                </div>
              )}
              
              {/* Campos específicos para gestor */}
              {formData.role === 'manager' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Permissões
                  </label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="manage_appointments"
                          name="permissions"
                          type="checkbox"
                          value="manage_appointments"
                          checked={formData.permissions.includes('manage_appointments')}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="manage_appointments" className="font-medium text-gray-700">
                          Gerenciar Agendamentos
                        </label>
                        <p className="text-gray-500">Confirmar, cancelar e reagendar compromissos</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="manage_professionals"
                          name="permissions"
                          type="checkbox"
                          value="manage_professionals"
                          checked={formData.permissions.includes('manage_professionals')}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="manage_professionals" className="font-medium text-gray-700">
                          Gerenciar Profissionais
                        </label>
                        <p className="text-gray-500">Adicionar, editar e remover profissionais</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="manage_services"
                          name="permissions"
                          type="checkbox"
                          value="manage_services"
                          checked={formData.permissions.includes('manage_services')}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="manage_services" className="font-medium text-gray-700">
                          Gerenciar Serviços
                        </label>
                        <p className="text-gray-500">Adicionar, editar e remover serviços</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="view_analytics"
                          name="permissions"
                          type="checkbox"
                          value="view_analytics"
                          checked={formData.permissions.includes('view_analytics')}
                          onChange={handleChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="view_analytics" className="font-medium text-gray-700">
                          Visualizar Relatórios
                        </label>
                        <p className="text-gray-500">Acessar relatórios e análises</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                >
                  {submitting ? 'Enviando...' : 'Enviar Convite'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      
      {/* Lista de convites pendentes */}
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-3">Convites Pendentes</h4>
        
        {invitations.length === 0 ? (
          <div className="bg-gray-50 p-4 rounded-md text-center text-gray-500">
            Nenhum convite pendente
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enviado em
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expira em
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => {
                  // Converter timestamps para datas legíveis
                  const createdAt = invitation.createdAt && invitation.createdAt.toDate 
                    ? invitation.createdAt.toDate() 
                    : new Date(invitation.createdAt?.seconds * 1000);
                    
                  const expiresAt = invitation.expiresAt && invitation.expiresAt.toDate 
                    ? invitation.expiresAt.toDate() 
                    : new Date(invitation.expiresAt?.seconds * 1000);
                    
                  return (
                    <tr key={invitation.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.role === 'professional' ? 'Profissional' : 'Gestor'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {createdAt.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {expiresAt.toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          type="button"
                          onClick={() => handleResend(invitation.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Reenviar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancel(invitation.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Cancelar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationManager; 